// One Klaviyo engagement segment (3.2), read-only: what the last daily refresh
// found — members in Klaviyo, of them contacts here, of those sendable — where
// it came from, which campaigns use it, and its log. Nothing to edit: its
// rules live in Klaviyo, and a save, a rebuild or an archive is refused by
// the API as well.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchKlaviyoSegments, fetchSegmentUsage } from "@/lib/api";
import type { KlaviyoSegment, SegmentDetail, SegmentUse } from "@/lib/api";
import { DetailList } from "@/components/emc/DetailList";
import { PageHeader } from "@/components/emc/PageHeader";
import { StatusPill } from "@/components/emc/StatusPill";
import { Callout, LoadingState } from "@/components/emc/states";
import { count, minute } from "@/components/emc/format";
import { statusLook } from "@/components/emc/status";
import { KlaviyoMark } from "@/pages/segments/KlaviyoSegments";
import { rowWords } from "@/pages/segments/klaviyo";

const ACTIONS: Record<string, string> = {
  klaviyo_followed: "átvéve a Klaviyóból",
  klaviyo_refreshed: "tagság frissítve",
  klaviyo_renamed: "átnevezve",
  klaviyo_gone: "a Klaviyóban megszűnt",
  klaviyo_back: "újra megjelent a Klaviyóban",
};

function eventText(action: string, toState: string | null): string {
  return ACTIONS[action] ?? (toState ? statusLook("segment", toState).label : action);
}

export function KlaviyoSegmentView({ segment }: { segment: SegmentDetail }) {
  const [row, setRow] = useState<KlaviyoSegment | null | undefined>(undefined);
  const [uses, setUses] = useState<SegmentUse[]>([]);

  useEffect(() => {
    let live = true;
    fetchKlaviyoSegments(segment.country)
      .then((rows) => live && setRow(rows.find((item) => item.segment_id === segment.id) ?? null))
      .catch(() => live && setRow(null));
    fetchSegmentUsage(segment.country)
      .then((answer) => live && setUses(answer.filter((use) => use.segment_id === segment.id)))
      .catch(() => live && setUses([]));
    return () => {
      live = false;
    };
  }, [segment.country, segment.id]);

  const words = row ? rowWords(row) : null;

  return (
    <>
      <PageHeader
        title={segment.name}
        back={{ to: "/szegmensek?nezet=klaviyo", label: "Klaviyo-szegmensek" }}
        meta={
          <>
            {words ? (
              <StatusPill tone={words.tone} title={words.standing}>
                {words.pill}
              </StatusPill>
            ) : (
              <StatusPill domain="segment" state={segment.state} />
            )}
            <KlaviyoMark />
          </>
        }
      />
      <div className="flex max-w-160 flex-col gap-6">
        <Callout>
          A Klaviyóból átvett szegmens: a tagságát naponta egyszer a Klaviyo adja, a Klaviyo-profil azonosítója
          alapján párosítva a(z) {segment.country} kontaktjaival. Itt nem szerkeszthető, nem építhető újra és nem
          archiválható — a feltételei a Klaviyóban vannak. Kampány közönségének vagy kizárásának választható; a
          leiratkozottakat és a kizárt címeket a kampány a küldéskor ebből is kiveszi.
        </Callout>
        {words && row && row.gone_at !== null && (
          <Callout tone="warn">
            A Klaviyo {minute(row.gone_at)} óta nem listázza ezt a szegmenst (törölték, vagy a nevéből kikerült az
            „engaged”). Az utolsó tagsága megmaradt, de kampány nem indulhat vele.
          </Callout>
        )}
        <section className="flex flex-col gap-3 rounded-card border border-line bg-surface p-4 shadow-emc">
          <h2>A legutóbbi frissítés</h2>
          {row === undefined ? (
            <LoadingState lines={4} />
          ) : row === null || words === null ? (
            <p className="text-muted-foreground">A Klaviyo-adatai nem olvashatók most.</p>
          ) : (
            <DetailList
              columns={2}
              items={[
                { label: "Tag a Klaviyóban", value: words.inKlaviyo },
                { label: "Ebből nálunk", value: words.found },
                { label: "Nincs meg nálunk", value: count(row.not_found_count) },
                { label: "Ebből küldhető", value: words.sendable },
                { label: "Frissítve", value: minute(row.refreshed_at), mono: true },
                { label: "Átvéve", value: minute(row.first_seen_at), mono: true },
                { label: "Név a Klaviyóban", value: row.klaviyo_name },
                { label: "Klaviyo-azonosító", value: row.klaviyo_id, mono: true },
              ]}
            />
          )}
          <p className="text-label text-muted-foreground">
            Küldhető: feliratkozott, és nincs rajta élő kizárás — sem globális, sem az országé. A kampány ezen
            felül a gyakorisági korlátot és a Klaviyo-átadás hullámát is alkalmazza; azt a kampány becslése mutatja.
          </p>
        </section>
        <section className="flex flex-col gap-3 rounded-card border border-line bg-surface p-4 shadow-emc">
          <h2>Kampányok, amelyek használják</h2>
          {uses.length === 0 ? (
            <p className="text-muted-foreground">Egy kampány sem.</p>
          ) : (
            <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
              {uses.map((use) => (
                <li key={`${use.campaign_id}-${use.role}`} className="flex flex-wrap items-center gap-2">
                  {use.group_id !== null ? (
                    <Link to={`/kampanyok/${use.group_id}`}>{use.campaign_name}</Link>
                  ) : (
                    <span>{use.campaign_name}</span>
                  )}
                  <StatusPill domain="campaign" state={use.campaign_state} />
                  <span className="text-muted-foreground">
                    {use.role === "exclude" ? "kizárásként" : "közönségként"}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
        <section className="flex flex-col gap-3 rounded-card border border-line bg-surface p-4 shadow-emc">
          <h2>Napló</h2>
          <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
            {segment.events.map((event, index) => (
              <li key={index} className="flex flex-wrap gap-x-3">
                <span className="data-mono text-muted-foreground">{minute(event.occurred_at)}</span>
                <span>{eventText(event.action, event.to_state)}</span>
                {typeof event.detail?.added === "number" && (
                  <span className="text-muted-foreground">
                    +{count(event.detail.added as number)} / −{count((event.detail.removed as number) ?? 0)}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </section>
      </div>
    </>
  );
}
